import React from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Grid, Card, CardActionArea, CardContent, Chip, Stack, Button,
  Breadcrumbs, Link as MuiLink,
} from '@mui/material';
import {
  Login as LoginIcon,
  Dashboard as DashIcon,
  OpenInBrowser as LaunchIcon,
  Timeline as ProgressIcon,
  GroupAdd as JoinIcon,
  Person as PersonIcon,
  ArrowBack as BackIcon,
  Android as AndroidIcon,
} from '@mui/icons-material';

import AndroidLoginMockup from './AndroidLoginMockup';
import AndroidDashboardMockup from './AndroidDashboardMockup';
import AndroidPartnerLaunchMockup from './AndroidPartnerLaunchMockup';
import AndroidProgressMockup from './AndroidProgressMockup';
import AndroidJoinClassMockup from './AndroidJoinClassMockup';
import AndroidProfileMockup from './AndroidProfileMockup';

const MOCKS = [
  {
    slug: 'login',
    title: 'Login',
    tagline: 'AchieveDXP dashboard login rendered in the Android app\'s parent WebView. Layer 1 — no backend auth work.',
    icon: <LoginIcon sx={{ fontSize: 28 }} />,
    color: '#1976d2',
    Component: AndroidLoginMockup,
  },
  {
    slug: 'dashboard',
    title: 'Dashboard',
    tagline: 'Post-login tile grid. Pull-to-refresh, bottom nav, daily goal card, partner app tiles with status chips.',
    icon: <DashIcon sx={{ fontSize: 28 }} />,
    color: '#14bf96',
    Component: AndroidDashboardMockup,
  },
  {
    slug: 'partner-launch',
    title: 'Partner Launch',
    tagline: 'Three stages of Layer 3 injection: preparing, auto-login (visible for review), WebView-wrapped Khan landed.',
    icon: <LaunchIcon sx={{ fontSize: 28 }} />,
    color: '#ff6b35',
    Component: AndroidPartnerLaunchMockup,
  },
  {
    slug: 'progress',
    title: 'Progress',
    tagline: 'Timeline, impact points, achievements, class roster. Empty state included.',
    icon: <ProgressIcon sx={{ fontSize: 28 }} />,
    color: '#7e57c2',
    Component: AndroidProgressMockup,
  },
  {
    slug: 'join-class',
    title: 'Join Class',
    tagline: '6-char code entry with OTP-style input, QR scan option, submitting and success states.',
    icon: <JoinIcon sx={{ fontSize: 28 }} />,
    color: '#5e35b1',
    Component: AndroidJoinClassMockup,
  },
  {
    slug: 'profile',
    title: 'Profile',
    tagline: 'Student identity, stat card, class memberships, settings list, sign out.',
    icon: <PersonIcon sx={{ fontSize: 28 }} />,
    color: '#ef6c00',
    Component: AndroidProfileMockup,
  },
];

const MockCard = ({ mock }) => (
  <Card elevation={0} sx={{
    border: '1px solid #e0e0e0', borderRadius: 3, height: '100%',
    transition: 'transform 0.15s, box-shadow 0.15s',
    '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 16px rgba(16,24,40,0.08)' },
  }}>
    <CardActionArea component={RouterLink} to={`/mockups/android/${mock.slug}`} sx={{ height: '100%' }}>
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1.5 }}>
          <Box sx={{
            width: 52, height: 52, borderRadius: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: `${mock.color}15`, color: mock.color,
          }}>
            {mock.icon}
          </Box>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              {mock.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              /mockups/android/{mock.slug}
            </Typography>
          </Box>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.82rem', lineHeight: 1.45 }}>
          {mock.tagline}
        </Typography>
      </CardContent>
    </CardActionArea>
  </Card>
);

const AndroidMockupIndex = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const selected = slug ? MOCKS.find(m => m.slug === slug) : null;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 4 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink component={RouterLink} to="/mockups" underline="hover" color="inherit">
          Mockups
        </MuiLink>
        <MuiLink component={RouterLink} to="/mockups/android" underline="hover" color="inherit">
          Android
        </MuiLink>
        {selected && <Typography color="text.primary">{selected.title}</Typography>}
      </Breadcrumbs>

      <Box sx={{ mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <AndroidIcon sx={{ fontSize: 32, color: '#3ddc84' }} />
          <Typography variant="h4" fontWeight={700}>
            AchieveDXP Android Port
          </Typography>
          <Chip label="MOCKUP" size="small" color="warning" sx={{ fontWeight: 700 }} />
        </Stack>
        <Typography variant="body1" color="text.secondary">
          Phone-frame mockups of the native Kotlin/Jetpack Compose + WebView design
          from <code>docs/ACHIEVEDXP_ANDROID_DESIGN.md</code>. These are web renderings —
          not a real Android app — used for design review before implementation.
        </Typography>
      </Box>

      {selected ? (
        <>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate('/mockups/android')}
            sx={{ mb: 1, textTransform: 'none' }}
          >
            Back to all Android mocks
          </Button>
          <Box sx={{ bgcolor: '#eef0f5', borderRadius: 4, py: 2, minHeight: 820 }}>
            <selected.Component />
          </Box>
        </>
      ) : (
        <Grid container spacing={2}>
          {MOCKS.map(mock => (
            <Grid item xs={12} sm={6} md={4} key={mock.slug}>
              <MockCard mock={mock} />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default AndroidMockupIndex;
